import { forwardRef, useMemo } from 'react';
import styled, { css } from 'styled-components';
import type { DefaultTheme, ThemedStyledProps } from 'styled-components';
import { transparentize } from 'color2k';
import { Colors, Fonts } from 'styles';
import { ChipProvider } from './useChip';
import { rootTheme } from 'theme/rootTheme';

const SIZE = {
  xs: css`
    font-size: 1.2rem;
    line-height: 1.4rem;
    padding: 2px 6px;
  `,
  sm: css`
    font-size: 1.2rem;
    line-height: 1.4rem;
    padding: 6px 12px;
  `,
  md: css`
    font-size: 1.4rem;
    line-height: 1.6rem;
    padding: 6px 12px;
  `,
  lg: css`
    font-size: 1.6rem;
    line-height: 2.2rem;
    padding: 6px 12px;
  `,
} as const;

const COLOR = {
  blue: css`
    ${({ variant }: { variant: ChipVariant }) => {
      if (variant === 'outline') {
        return css`
          background: ${transparentize(Colors.blue2, 0.88)};
          border: 1px solid ${Colors.blue2};
        `;
      }
      if (variant === 'solid') {
        return css`
          background: ${Colors.blue2};
          border: 1px solid transparent;
        `;
      }
    }}
  `,
  gray: css`
    ${({ variant }: { variant: ChipVariant }) => {
      if (variant === 'outline') {
        return css`
          background: transparent;
          border: 1px solid ${Colors.gray8};
        `;
      }
      if (variant === 'solid') {
        return css`
          background: ${Colors.gray5};
          border: 1px solid transparent;
        `;
      }
    }}
  `,
} as const;

export type ChipSize = 'xs' | 'sm' | 'md' | 'lg';
export type ChipVariant = 'solid' | 'outline';
export type ChipColor = 'blue' | 'gray';
export type ChipBorder = 'rounded' | 'square';

export type ChipProps = {
  size?: ChipSize;
  variant?: ChipVariant;
  color?: ChipColor;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  children: React.ReactNode;
  disableOverlay?: boolean;
  border?: ChipBorder;
};

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function ForwardedChip(
  {
    size = 'md',
    variant = 'solid',
    color = 'gray',
    disabled = false,
    onClick,
    onMouseDown,
    className,
    children,
    disableOverlay = false,
    border,
    ...rest
  },
  ref
) {
  const contextValue = useMemo(
    () => ({
      size,
      variant,
      color,
      disabled,
    }),
    [size, variant, color, disabled]
  );
  return (
    <ChipProvider value={contextValue}>
      <BaseChip
        {...rest}
        type="button"
        ref={ref}
        variant={variant}
        size={size}
        color={color}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        onMouseDown={disabled ? undefined : onMouseDown}
        className={className}
        border={border}
      >
        {children}
        {!disabled && !disableOverlay ? <ChipOverlay border={border} /> : null}
      </BaseChip>
    </ChipProvider>
  );
});

type BaseChipProps = {
  size: NonNullable<ChipProps['size']>;
  variant: NonNullable<ChipProps['variant']>;
  color: NonNullable<ChipProps['color']>;
  disabled: NonNullable<ChipProps['disabled']>;
};

export const BaseChip = styled.button`
  ${(props: ThemedStyledProps<BaseChipProps, DefaultTheme>) => COLOR[props.color]}
  ${(props: ThemedStyledProps<BaseChipProps, DefaultTheme>) => SIZE[props.size]}
  ${(props: ThemedStyledProps<BaseChipProps, DefaultTheme>) =>
    props.disabled &&
    css`
      & {
        background: ${Colors.gray6};
        cursor: not-allowed;
        border: 1px solid transparent;
      }
    `}

  position: relative;
  display: inline-flex;
  align-items: center;

  cursor: pointer;
  color: ${Colors.gray10};
  font-family: ${Fonts.mainFont};

  border-radius: ${(
    props: ThemedStyledProps<BaseChipProps, DefaultTheme> & { border?: ChipBorder }
  ) => (props.border === 'square' ? rootTheme.radius.sm : rootTheme.radius.xxl)}px;

  &:focus,
  &:focus-within {
    border-color: ${(props: ThemedStyledProps<BaseChipProps, DefaultTheme>) =>
      props.color === 'blue' ? Colors.blue5 : Colors.gray8};
    box-shadow: ${(props: ThemedStyledProps<BaseChipProps, DefaultTheme>) =>
        transparentize(props.color === 'blue' ? Colors.blue5 : Colors.gray8, 0.88)}
      0 0 0 4px;
  }
`;

export const ChipOverlay = styled.span<{ border?: ChipBorder }>`
  position: absolute;
  padding: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  border-radius: ${(props: { border?: ChipBorder }) =>
    props.border === 'square' ? rootTheme.radius.sm : rootTheme.radius.xxl}px;

  pointer-events: none;

  background-color: ${transparentize(Colors.gray10, 1)};

  ${BaseChip}:hover &, ${BaseChip}:focus & {
    background-color: ${transparentize(Colors.gray10, 0.85)};
  }

  ${BaseChip}:active & {
    background-color: ${transparentize(Colors.gray10, 0.7)};
  }
`;
